import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getIngestFileTypes,
  createIngestFileType,
  updateIngestFileType,
  deleteIngestFileType,
  getOutputFileTypes,
  createOutputFileType,
  updateOutputFileType,
  deleteOutputFileType,
  getConversionApis,
  createConversionApi,
  updateConversionApi,
  deleteConversionApi,
  exportDefinitionsConfig,
  importDefinitionsConfig,
} from "../api/adminApi";
import { getOrgAiSettings, saveOrgAiSettings, testOrgAiSettings } from "../api/aiSettingsApi";
import { Hero } from "../components/adminSurface/AdminSurface";
import { SchemaArt } from "../components/adminSurface/heroArt";
import { getSiteUrl, reconfigureSiteUrl } from "../api/updateApi";

// ── Category registry ─────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    key: "ingestFileTypes",
    label: "Ingest File Types",
    description: "Accepted file types for bulk and ad-hoc ingestion.",
  },
  {
    key: "outputFileTypes",
    label: "Output File Types",
    description: "Allowed rendering output formats.",
  },
  {
    key: "conversionApis",
    label: "Conversion APIs",
    description: "Format conversion and postprocessing API mappings.",
  },
  {
    key: "orgAiSettings",
    label: "Organization AI Settings",
    description: "LLM API key and model for AI-powered search.",
  },
  {
    key: "siteUrl",
    label: "Site URL",
    description: "Public URL used by remote clients to reach this server.",
  },
];

// ── Shared helpers ────────────────────────────────────────────────────────────

function Alert({ type = "info", children, onClose }) {
  const palette = {
    error:   { bg: "#ffe9e9", text: "#8b0000",  border: "#f3c6c6" },
    success: { bg: "#e9f7ef", text: "#116622",  border: "#b8e6c8" },
    info:    { bg: "#f6f8ff", text: "#264b9a",  border: "#d0dff5" },
  }[type] ?? {};
  return (
    <div style={{
      background: palette.bg, color: palette.text,
      border: `1px solid ${palette.border}`,
      borderRadius: 8, padding: "10px 14px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: 14,
    }}>
      <span style={{ fontWeight: 600 }}>{children}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, marginLeft: 12, color: palette.text }}>✕</button>
      )}
    </div>
  );
}

// ── Ingest File Types panel ───────────────────────────────────────────────────

const MODE_OPTIONS = [
  { value: "BulkContainer", label: "Bulk Container only" },
  { value: "AdHoc",         label: "Ad-Hoc only" },
  { value: "Both",          label: "Both modes" },
];

const EMPTY_FORM = { typeId: "", label: "", mode: "Both", sortOrder: 0 };

function IngestFileTypeModal({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);
  const isEdit = !!(initial?.id);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard" onClick={e => e.stopPropagation()}>
        <button className="drawer-close-btn drawer-close-btn-abs" onClick={onClose} aria-label="Close">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalTitle">{isEdit ? "Edit file type" : "Add file type"}</div>

        <div style={{ width: "100%", marginTop: 20 }}>
          <div style={{ marginBottom: 14 }}>
            <label className="createDbLabel">
              Type ID <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              autoFocus
              className="createDbInput"
              value={form.typeId}
              onChange={e => set("typeId", e.target.value.toUpperCase())}
              placeholder="e.g. AFP, PDF, DOCX"
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Stored identifier — auto-uppercased.</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="createDbLabel">
              Label <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              className="createDbInput"
              value={form.label}
              onChange={e => set("label", e.target.value)}
              placeholder="e.g. PostScript, Word / DOCX"
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="createDbLabel">Applies to</label>
            <select className="createDbInput" value={form.mode} onChange={e => set("mode", e.target.value)}>
              {MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: isEdit ? 14 : 0 }}>
            <label className="createDbLabel">Sort order</label>
            <input
              type="number"
              className="createDbInput"
              value={form.sortOrder}
              min={0}
              step={1}
              onChange={e => set("sortOrder", parseInt(e.target.value) || 0)}
              style={{ width: 100 }}
            />
          </div>

          {isEdit && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={form.isActive ?? true} onChange={e => set("isActive", e.target.checked)} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>Active</span>
            </label>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button type="button" className="dbModalCancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              type="button"
              className="primary-btn"
              disabled={saving || !form.typeId.trim() || !form.label.trim()}
              onClick={() => onSave(form)}
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IngestFileTypesPanel({ token }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [modal, setModal]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const msgTimer = useRef(null);

  function showSuccess(msg) {
    setSuccess(msg);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setSuccess(""), 3000);
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await getIngestFileTypes(token);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  async function handleSave(form) {
    setSaving(true);
    try {
      if (modal?.item?.id) {
        await updateIngestFileType(token, { id: modal.item.id, ...form });
        showSuccess("File type updated.");
      } else {
        await createIngestFileType(token, form);
        showSuccess("File type added.");
      }
      setModal(null);
      await load();
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.label}" (${item.typeId})?`)) return;
    setDeleting(item.id);
    try {
      await deleteIngestFileType(token, item.id);
      showSuccess("File type deleted.");
      await load();
    } catch (e) {
      setError(e?.message || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  }

  const modeLabel = (m) => MODE_OPTIONS.find(o => o.value === m)?.label ?? m;
  const modeBadge = (m) => ({
    background: m === "BulkContainer" ? "#eff6ff" : m === "AdHoc" ? "#f0fdf4" : "#faf5ff",
    color:      m === "BulkContainer" ? "#1d4ed8" : m === "AdHoc" ? "#15803d" : "#6d28d9",
    borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600,
  });

  return (
    <div className="indexes-main">
      {error   && <Alert type="error"   onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess("")}>{success}</Alert>}

      <div className="dbTabIndexActions">
        <button type="button" className="chip-btn indexes-add-index-chip" onClick={() => setModal({ mode: "add" })}>
          + Add type
        </button>
      </div>

      {loading ? (
        <div style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>Loading…</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 90 }}>Type ID</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Label</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 170 }}>Applies to</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 70 }}>Order</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 70 }}>Active</th>
              <th style={{ padding: "8px 12px", width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
                  No file types defined. Click "+ Add type" to create one.
                </td>
              </tr>
            ) : items.map(item => (
              <tr key={item.id} style={{ borderBottom: "1px solid #f3f4f6", opacity: item.isActive ? 1 : 0.5 }}>
                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>{item.typeId}</td>
                <td style={{ padding: "8px 12px" }}>{item.label}</td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={modeBadge(item.mode)}>{modeLabel(item.mode)}</span>
                </td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: "#9ca3af" }}>{item.sortOrder}</td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: item.isActive ? "#15803d" : "#9ca3af", fontWeight: 700 }}>
                  {item.isActive ? "✓" : "—"}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      title="Edit"
                      onClick={() => setModal({ mode: "edit", item })}
                      style={{ background: "#f0f4ff", border: "1.5px solid #bfcfff", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      disabled={deleting === item.id}
                      onClick={() => handleDelete(item)}
                      style={{ background: "#fff5f5", border: "1.5px solid #fca5a5", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      {deleting === item.id
                        ? <span style={{ fontSize: 11 }}>…</span>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                      }
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <IngestFileTypeModal
          initial={modal.item ?? null}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Output File Types panel ───────────────────────────────────────────────────

const EMPTY_OUTPUT_FORM = { typeId: "", label: "", sortOrder: 0 };

function OutputFileTypeModal({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial ?? EMPTY_OUTPUT_FORM);
  const isEdit = !!(initial?.id);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard" onClick={e => e.stopPropagation()}>
        <button className="drawer-close-btn drawer-close-btn-abs" onClick={onClose} aria-label="Close">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalTitle">{isEdit ? "Edit output type" : "Add output type"}</div>

        <div style={{ width: "100%", marginTop: 20 }}>
          <div style={{ marginBottom: 14 }}>
            <label className="createDbLabel">
              Type ID <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              autoFocus
              className="createDbInput"
              value={form.typeId}
              onChange={e => set("typeId", e.target.value.toUpperCase())}
              placeholder="e.g. PDF, PNG, TIFF"
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Stored identifier — auto-uppercased.</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="createDbLabel">
              Label <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              className="createDbInput"
              value={form.label}
              onChange={e => set("label", e.target.value)}
              placeholder="e.g. PDF, PNG, TIFF"
            />
          </div>

          <div style={{ marginBottom: isEdit ? 14 : 0 }}>
            <label className="createDbLabel">Sort order</label>
            <input
              type="number"
              className="createDbInput"
              value={form.sortOrder}
              min={0}
              step={1}
              onChange={e => set("sortOrder", parseInt(e.target.value) || 0)}
              style={{ width: 100 }}
            />
          </div>

          {isEdit && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={form.isActive ?? true} onChange={e => set("isActive", e.target.checked)} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>Active</span>
            </label>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button type="button" className="dbModalCancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              type="button"
              className="primary-btn"
              disabled={saving || !form.typeId.trim() || !form.label.trim()}
              onClick={() => onSave(form)}
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OutputFileTypesPanel({ token }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [modal, setModal]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);
  const msgTimer = useRef(null);

  function showSuccess(msg) {
    setSuccess(msg);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setSuccess(""), 3000);
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await getOutputFileTypes(token);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  async function handleSave(form) {
    setSaving(true);
    try {
      if (modal?.item?.id) {
        await updateOutputFileType(token, { id: modal.item.id, ...form });
        showSuccess("Output type updated.");
      } else {
        await createOutputFileType(token, form);
        showSuccess("Output type added.");
      }
      setModal(null);
      await load();
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.label}" (${item.typeId})?`)) return;
    setDeleting(item.id);
    try {
      await deleteOutputFileType(token, item.id);
      showSuccess("Output type deleted.");
      await load();
    } catch (e) {
      setError(e?.message || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="indexes-main">
      {error   && <Alert type="error"   onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess("")}>{success}</Alert>}

      <div className="dbTabIndexActions">
        <button type="button" className="chip-btn indexes-add-index-chip" onClick={() => setModal({ mode: "add" })}>
          + Add type
        </button>
      </div>

      {loading ? (
        <div style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>Loading…</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 90 }}>Type ID</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Label</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 70 }}>Order</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 70 }}>Active</th>
              <th style={{ padding: "8px 12px", width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
                  No output types defined. Click "+ Add type" to create one.
                </td>
              </tr>
            ) : items.map(item => (
              <tr key={item.id} style={{ borderBottom: "1px solid #f3f4f6", opacity: item.isActive ? 1 : 0.5 }}>
                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>{item.typeId}</td>
                <td style={{ padding: "8px 12px" }}>{item.label}</td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: "#9ca3af" }}>{item.sortOrder}</td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: item.isActive ? "#15803d" : "#9ca3af", fontWeight: 700 }}>
                  {item.isActive ? "✓" : "—"}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      title="Edit"
                      onClick={() => setModal({ mode: "edit", item })}
                      style={{ background: "#f0f4ff", border: "1.5px solid #bfcfff", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      disabled={deleting === item.id}
                      onClick={() => handleDelete(item)}
                      style={{ background: "#fff5f5", border: "1.5px solid #fca5a5", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      {deleting === item.id
                        ? <span style={{ fontSize: 11 }}>…</span>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                      }
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <OutputFileTypeModal
          initial={modal.item ?? null}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Conversion APIs panel ─────────────────────────────────────────────────────

const HTTP_METHODS = ["POST", "GET", "PUT", "PATCH"];

const EMPTY_CONV_FORM = {
  inputTypeId: "", outputTypeId: "", apiUrl: "",
  httpMethod: "POST", headersJson: "", bodyTemplate: "",
  timeoutSeconds: 30, notes: "",
  authMode: "None",
  authStaticToken: "",
  tokenEndpointUrl: "", tokenEndpointMethod: "POST",
  tokenEndpointBody: "", tokenEndpointHeaders: "",
  tokenResponseField: "access_token", tokenCacheSeconds: 3600,
  responseOutputField: "",
};

const AUTH_MODE_OPTIONS = [
  { value: "None",          label: "None" },
  { value: "StaticBearer",  label: "Static Bearer token" },
  { value: "TokenEndpoint", label: "Token endpoint (OAuth2 / custom)" },
];

// ── Header presets ─────────────────────────────────────────────────────────────

const REQUEST_HEADER_PRESETS = [
  { label: "JSON (Content-Type + Accept)",     value: '{"Content-Type": "application/json", "Accept": "application/json"}' },
  { label: "JSON Content-Type only",           value: '{"Content-Type": "application/json"}' },
  { label: "Accept JSON only",                 value: '{"Accept": "application/json"}' },
  { label: "Binary / Octet-stream",            value: '{"Content-Type": "application/octet-stream"}' },
  { label: "Multipart form-data",              value: '{"Content-Type": "multipart/form-data"}' },
  { label: "XML",                              value: '{"Content-Type": "application/xml", "Accept": "application/xml"}' },
];

const TOKEN_HEADER_PRESETS = [
  { label: "OAuth2 form-encoded (standard)",   value: '{"Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json"}' },
  { label: "Form-encoded only",                value: '{"Content-Type": "application/x-www-form-urlencoded"}' },
  { label: "JSON token request",               value: '{"Content-Type": "application/json", "Accept": "application/json"}' },
];

function HeadersPresetPicker({ presets, onSelect, current }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  function pick(preset) {
    // If there's existing content, ask before overwriting
    if (current && current.trim() && current.trim() !== preset.value) {
      const merged = (() => {
        try {
          const existing = JSON.parse(current);
          const incoming = JSON.parse(preset.value);
          return JSON.stringify({ ...existing, ...incoming }, null, 2);
        } catch {
          return null;
        }
      })();
      if (merged && window.confirm("Merge with existing headers? (Cancel to replace)")) {
        onSelect(merged);
      } else {
        onSelect(preset.value);
      }
    } else {
      onSelect(preset.value);
    }
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5,
          border: "1.5px solid #bfcfff", background: open ? "#e8efff" : "#f0f4ff",
          color: "#2563eb", cursor: "pointer", lineHeight: 1.6,
        }}
      >
        Presets ▾
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, zIndex: 200,
          background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          minWidth: 280, marginTop: 4, overflow: "hidden",
        }}>
          {presets.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(p)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 14px", border: "none", background: "none",
                fontSize: 12, color: "#374151", cursor: "pointer",
                borderBottom: i < presets.length - 1 ? "1px solid #f3f4f6" : "none",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ fontWeight: 600, color: "#1d4ed8", marginBottom: 2 }}>{p.label}</div>
              <div style={{ fontFamily: "monospace", fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.value}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversionApiModal({ initial, ingestTypes, outputTypes, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial ?? EMPTY_CONV_FORM);
  const [tab, setTab] = useState("basic"); // "basic" | "auth" | "advanced"
  const isEdit = !!(initial?.id);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tabStyle = (t) => ({
    padding: "6px 14px", fontSize: 12, fontWeight: 600, border: "none",
    borderBottom: tab === t ? "2px solid #1a6cbc" : "2px solid transparent",
    background: "none", cursor: "pointer",
    color: tab === t ? "#1a6cbc" : "#6b7280",
  });

  const authMode = form.authMode || "None";

  // Show a badge on the Auth tab if auth is configured
  const authConfigured = authMode !== "None";

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard" style={{ width: 520, maxWidth: "calc(100vw - 32px)" }} onClick={e => e.stopPropagation()}>
        <button className="drawer-close-btn drawer-close-btn-abs" onClick={onClose} aria-label="Close">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalTitle">{isEdit ? "Edit conversion API" : "Add conversion API"}</div>

        {/* Tab bar */}
        <div style={{ width: "100%", display: "flex", borderBottom: "1px solid #e5e7eb", marginTop: 16, marginBottom: 18 }}>
          <button style={tabStyle("basic")} onClick={() => setTab("basic")}>Mapping &amp; Endpoint</button>
          <button style={tabStyle("auth")} onClick={() => setTab("auth")}>
            Authentication
            {authConfigured && (
              <span style={{ marginLeft: 5, background: "#1a6cbc", color: "#fff", borderRadius: 8, fontSize: 10, padding: "1px 6px", verticalAlign: "middle" }}>
                {authMode === "StaticBearer" ? "Bearer" : "Token"}
              </span>
            )}
          </button>
          <button style={tabStyle("advanced")} onClick={() => setTab("advanced")}>Request Config</button>
        </div>

        <div style={{ width: "100%" }}>

          {/* ── Tab: Mapping & Endpoint ── */}
          {tab === "basic" && (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="createDbLabel">Input type <span style={{ color: "#ef4444" }}>*</span></label>
                  <select className="createDbInput" value={form.inputTypeId} onChange={e => set("inputTypeId", e.target.value)}>
                    <option value="">— select —</option>
                    {ingestTypes.map(t => <option key={t.typeId} value={t.typeId}>{t.typeId} — {t.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="createDbLabel">Output type <span style={{ color: "#ef4444" }}>*</span></label>
                  <select className="createDbInput" value={form.outputTypeId} onChange={e => set("outputTypeId", e.target.value)}>
                    <option value="">— select —</option>
                    {outputTypes.map(t => <option key={t.typeId} value={t.typeId}>{t.typeId} — {t.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="createDbLabel">API URL <span style={{ color: "#ef4444" }}>*</span></label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select className="createDbInput" value={form.httpMethod} onChange={e => set("httpMethod", e.target.value)} style={{ width: 90, flexShrink: 0 }}>
                    {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    autoFocus
                    className="createDbInput"
                    value={form.apiUrl}
                    onChange={e => set("apiUrl", e.target.value)}
                    placeholder="https://converter.example.com/convert"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginBottom: isEdit ? 14 : 0 }}>
                <div style={{ width: 110 }}>
                  <label className="createDbLabel">Timeout (s)</label>
                  <input type="number" className="createDbInput" value={form.timeoutSeconds} min={1} step={1}
                    onChange={e => set("timeoutSeconds", parseInt(e.target.value) || 30)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="createDbLabel">Notes</label>
                  <input className="createDbInput" value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional description" />
                </div>
              </div>

              {isEdit && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.isActive ?? true} onChange={e => set("isActive", e.target.checked)} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>Active</span>
                </label>
              )}
            </>
          )}

          {/* ── Tab: Authentication ── */}
          {tab === "auth" && (
            <>
              <div style={{ marginBottom: 18 }}>
                <label className="createDbLabel">Authentication mode</label>
                <select className="createDbInput" value={authMode} onChange={e => set("authMode", e.target.value)}>
                  {AUTH_MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {authMode === "None" && (
                <div style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, color: "#6b7280" }}>
                  No authentication. Use request headers in the <strong>Request Config</strong> tab for API keys or custom auth headers.
                </div>
              )}

              {authMode === "StaticBearer" && (
                <div style={{ marginBottom: 4 }}>
                  <label className="createDbLabel">Bearer token <span style={{ color: "#ef4444" }}>*</span></label>
                  <input
                    className="createDbInput"
                    type="password"
                    value={form.authStaticToken}
                    onChange={e => set("authStaticToken", e.target.value)}
                    placeholder="eyJhbGciOiJSUzI1NiIs..."
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                  />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                    Sent as <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>Authorization: Bearer &lt;token&gt;</code> on every request.
                  </div>
                </div>
              )}

              {authMode === "TokenEndpoint" && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label className="createDbLabel">Token endpoint URL <span style={{ color: "#ef4444" }}>*</span></label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="createDbInput" value={form.tokenEndpointMethod || "POST"}
                        onChange={e => set("tokenEndpointMethod", e.target.value)} style={{ width: 90, flexShrink: 0 }}>
                        {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <input
                        className="createDbInput"
                        value={form.tokenEndpointUrl}
                        onChange={e => set("tokenEndpointUrl", e.target.value)}
                        placeholder="https://auth.example.com/oauth/token"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label className="createDbLabel">Token request body</label>
                    <textarea
                      className="createDbInput"
                      value={form.tokenEndpointBody}
                      onChange={e => set("tokenEndpointBody", e.target.value)}
                      placeholder={'grant_type=client_credentials&client_id=...&client_secret=...'}
                      rows={3}
                      style={{ resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                    />
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Form-encoded or JSON body sent to the token endpoint.</div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <label className="createDbLabel" style={{ marginBottom: 0 }}>Token endpoint headers (JSON)</label>
                      <HeadersPresetPicker
                        presets={TOKEN_HEADER_PRESETS}
                        current={form.tokenEndpointHeaders}
                        onSelect={v => set("tokenEndpointHeaders", v)}
                      />
                    </div>
                    <textarea
                      className="createDbInput"
                      value={form.tokenEndpointHeaders}
                      onChange={e => set("tokenEndpointHeaders", e.target.value)}
                      placeholder={'{"Content-Type": "application/x-www-form-urlencoded"}'}
                      rows={2}
                      style={{ resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <label className="createDbLabel">Token field in response</label>
                      <input
                        className="createDbInput"
                        value={form.tokenResponseField}
                        onChange={e => set("tokenResponseField", e.target.value)}
                        placeholder="access_token"
                      />
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>JSON field name containing the token value.</div>
                    </div>
                    <div style={{ width: 120 }}>
                      <label className="createDbLabel">Cache (s)</label>
                      <input
                        type="number"
                        className="createDbInput"
                        value={form.tokenCacheSeconds ?? 3600}
                        min={0}
                        step={60}
                        onChange={e => set("tokenCacheSeconds", parseInt(e.target.value) || 3600)}
                      />
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Seconds to reuse the token before re-fetching.</div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Tab: Request Config ── */}
          {tab === "advanced" && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <label className="createDbLabel" style={{ marginBottom: 0 }}>Request headers (JSON)</label>
                  <HeadersPresetPicker
                    presets={REQUEST_HEADER_PRESETS}
                    current={form.headersJson}
                    onSelect={v => set("headersJson", v)}
                  />
                </div>
                <textarea
                  className="createDbInput"
                  value={form.headersJson}
                  onChange={e => set("headersJson", e.target.value)}
                  placeholder={'{"X-Api-Key": "...", "Accept": "application/json"}'}
                  rows={4}
                  style={{ resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>JSON object of additional HTTP headers. Authorization is handled in the Authentication tab.</div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="createDbLabel">Request body template</label>
                <textarea
                  className="createDbInput"
                  value={form.bodyTemplate}
                  onChange={e => set("bodyTemplate", e.target.value)}
                  placeholder={'{"inputFile": "{inputFile}", "outputFormat": "{outputFormat}"}'}
                  rows={5}
                  style={{ resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                  Template for the POST body. Placeholders: <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{"{inputFile}"}</code> <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{"{outputFormat}"}</code>
                </div>
              </div>

              <div style={{ marginBottom: 4 }}>
                <label className="createDbLabel">Response output field</label>
                <input
                  className="createDbInput"
                  value={form.responseOutputField}
                  onChange={e => set("responseOutputField", e.target.value)}
                  placeholder="e.g. outputBase64 or data.fileContent"
                />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                  If the API returns a JSON envelope, enter the field name that contains the Base64-encoded output file. Leave empty if the API returns raw binary bytes directly.
                </div>
              </div>
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button type="button" className="dbModalCancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              type="button"
              className="primary-btn"
              disabled={saving || !form.inputTypeId || !form.outputTypeId || !form.apiUrl.trim()}
              onClick={() => onSave(form)}
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversionApisPanel({ token }) {
  const [items, setItems]         = useState([]);
  const [ingestTypes, setIngest]  = useState([]);
  const [outputTypes, setOutput]  = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [modal, setModal]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const msgTimer = useRef(null);

  function showSuccess(msg) {
    setSuccess(msg);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setSuccess(""), 3000);
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [convs, ingests, outputs] = await Promise.all([
        getConversionApis(token),
        getIngestFileTypes(token),
        getOutputFileTypes(token),
      ]);
      setItems(Array.isArray(convs) ? convs : []);
      setIngest(Array.isArray(ingests) ? ingests.filter(t => t.isActive) : []);
      setOutput(Array.isArray(outputs) ? outputs.filter(t => t.isActive) : []);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  async function handleSave(form) {
    setSaving(true);
    try {
      if (modal?.item?.id) {
        await updateConversionApi(token, { id: modal.item.id, ...form });
        showSuccess("Conversion API updated.");
      } else {
        await createConversionApi(token, form);
        showSuccess("Conversion API added.");
      }
      setModal(null);
      await load();
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete conversion API: ${item.inputTypeId} → ${item.outputTypeId}?`)) return;
    setDeleting(item.id);
    try {
      await deleteConversionApi(token, item.id);
      showSuccess("Conversion API deleted.");
      await load();
    } catch (e) {
      setError(e?.message || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  }

  // open edit modal pre-populated, converting null/undefined to ""
  function openEdit(item) {
    setModal({
      mode: "edit",
      item: {
        ...item,
        headersJson:          item.headersJson          ?? "",
        bodyTemplate:         item.bodyTemplate         ?? "",
        notes:                item.notes                ?? "",
        authMode:             item.authMode             ?? "None",
        authStaticToken:      item.authStaticToken      ?? "",
        tokenEndpointUrl:     item.tokenEndpointUrl     ?? "",
        tokenEndpointMethod:  item.tokenEndpointMethod  ?? "POST",
        tokenEndpointBody:    item.tokenEndpointBody    ?? "",
        tokenEndpointHeaders: item.tokenEndpointHeaders ?? "",
        tokenResponseField:   item.tokenResponseField   ?? "access_token",
        tokenCacheSeconds:    item.tokenCacheSeconds    ?? 3600,
        responseOutputField:  item.responseOutputField  ?? "",
      },
    });
  }

  return (
    <div className="indexes-main">
      {error   && <Alert type="error"   onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess("")}>{success}</Alert>}

      <div className="dbTabIndexActions">
        <button type="button" className="chip-btn indexes-add-index-chip" onClick={() => setModal({ mode: "add" })}>
          + Add mapping
        </button>
      </div>

      {loading ? (
        <div style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>Loading…</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 80 }}>Input</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 80 }}>Output</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 60 }}>Method</th>
              <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>API URL</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 60 }}>T/O</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600, width: 60 }}>Active</th>
              <th style={{ padding: "8px 12px", width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
                  No conversion APIs defined. Click "+ Add mapping" to create one.
                </td>
              </tr>
            ) : items.map(item => (
              <tr key={item.id} style={{ borderBottom: "1px solid #f3f4f6", opacity: item.isActive ? 1 : 0.5 }}>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12, background: "#eff6ff", color: "#1d4ed8", borderRadius: 4, padding: "2px 6px" }}>{item.inputTypeId}</span>
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12, background: "#f0fdf4", color: "#15803d", borderRadius: 4, padding: "2px 6px" }}>{item.outputTypeId}</span>
                </td>
                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>{item.httpMethod}</td>
                <td style={{ padding: "8px 12px", maxWidth: 260 }}>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#374151" }} title={item.apiUrl}>
                    {item.apiUrl}
                  </span>
                  {item.notes && <span style={{ fontSize: 11, color: "#9ca3af" }}>{item.notes}</span>}
                </td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: "#9ca3af", fontSize: 12 }}>{item.timeoutSeconds}s</td>
                <td style={{ padding: "8px 12px", textAlign: "center", color: item.isActive ? "#15803d" : "#9ca3af", fontWeight: 700 }}>
                  {item.isActive ? "✓" : "—"}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button
                      type="button" title="Edit"
                      onClick={() => openEdit(item)}
                      style={{ background: "#f0f4ff", border: "1.5px solid #bfcfff", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button" title="Delete"
                      disabled={deleting === item.id}
                      onClick={() => handleDelete(item)}
                      style={{ background: "#fff5f5", border: "1.5px solid #fca5a5", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      {deleting === item.id
                        ? <span style={{ fontSize: 11 }}>…</span>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                      }
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <ConversionApiModal
          initial={modal.item ?? null}
          ingestTypes={ingestTypes}
          outputTypes={outputTypes}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Org AI Settings panel ─────────────────────────────────────────────────────

// Soft hint only — never blocks save. Runtime error handling surfaces the actual upstream response.
function validateLlmEndpoint(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return { kind: "ok", message: "" };
  let url;
  try { url = new URL(trimmed); } catch {
    return { kind: "warn", message: "This doesn't look like a valid URL. Expected something like https://api.openai.com/v1/chat/completions." };
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { kind: "warn", message: "Only http:// or https:// URLs will work." };
  }
  if (/\/v1\/?$/i.test(url.pathname)) {
    return { kind: "warn", message: "'/v1' is OpenAI's base path, not a chat endpoint. You probably want '…/v1/chat/completions'." };
  }
  if (!/completions\/?$/i.test(url.pathname)) {
    return { kind: "warn", message: "Endpoint doesn't end in '…/completions'. If this isn't an OpenAI-compatible chat-completions URL, the LLM call will fail." };
  }
  return { kind: "ok", message: "" };
}

function OrgAiSettingsPanel({ token }) {
  const [serverState, setServerState] = useState({ hasKey: false, model: "", endpoint: "" });
  const [newKey, setNewKey] = useState("");
  const [clearKey, setClearKey] = useState(false);
  const [model, setModel] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getOrgAiSettings(token);
      setServerState(data);
      setModel(data.model || "");
      setEndpoint(data.endpoint || "");
    } catch (e) {
      setError(e.message || "Failed to load settings.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const endpointHint = useMemo(() => validateLlmEndpoint(endpoint), [endpoint]);

  const handleTest = async () => {
    setTestResult(null);
    setTesting(true);
    try {
      const res = await testOrgAiSettings(token, {
        apiKey: clearKey ? "" : (newKey || null),
        model,
        endpoint,
      });
      setTestResult(res);
    } catch (e) {
      setTestResult({ ok: false, message: e.message || "Test request failed." });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const data = await saveOrgAiSettings(token, {
        apiKey: clearKey ? "" : (newKey || null),
        clearKey,
        model,
        endpoint,
      });
      setServerState(data);
      setNewKey("");
      setClearKey(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="indexes-main" style={{ maxWidth: 560 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Organization AI Settings</h3>
      <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
        Set an organization-wide LLM API key. Users without a personal key will use this key
        automatically. Personal keys (set in Preferences) always take priority.
      </p>

      {error && <Alert type="error" onClose={() => setError("")}>{error}</Alert>}
      {saved && <Alert type="success">Saved successfully.</Alert>}

      {/* API Key */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          API Key
          {serverState.hasKey && !clearKey && (
            <span style={{ fontWeight: 400, color: "#2a7a2a", marginLeft: 8, fontSize: 12 }}>
              ● Key configured
            </span>
          )}
        </label>
        {serverState.hasKey && !clearKey ? (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="password"
              value="••••••••••••••••••••••••"
              readOnly
              style={{ flex: 1, padding: "7px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, color: "#555", background: "#fafafa" }}
            />
            <button
              onClick={() => { setClearKey(true); setNewKey(""); }}
              disabled={saving}
              style={{ padding: "6px 14px", fontSize: 13, border: "1.5px solid #dc2626", borderRadius: 6, background: "#fff5f5", color: "#dc2626", cursor: "pointer" }}
            >
              Clear key
            </button>
          </div>
        ) : (
          <input
            type="password"
            value={newKey}
            onChange={(e) => { setNewKey(e.target.value); setClearKey(false); }}
            placeholder={clearKey ? "Key will be cleared on save" : "sk-..."}
            disabled={saving}
            style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
          />
        )}
      </div>

      {/* Model */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Model <span style={{ fontWeight: 400, color: "#888", fontSize: 12 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="gpt-4o-mini"
          disabled={saving}
          style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
        />
      </div>

      {/* Endpoint */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          LLM Endpoint <span style={{ fontWeight: 400, color: "#888", fontSize: 12 }}>(optional, defaults to OpenAI)</span>
        </label>
        <input
          type="text"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder="https://api.openai.com/v1/chat/completions"
          disabled={saving}
          style={{ width: "100%", padding: "7px 10px", border: `1.5px solid ${endpointHint.kind === "warn" ? "#d97706" : "#e5e7eb"}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
        />
        {endpointHint.message && (
          <div style={{ fontSize: 11, marginTop: 4, color: endpointHint.kind === "warn" ? "#d97706" : "#9ca3af" }}>
            {endpointHint.message}
          </div>
        )}
      </div>

      {testResult && (
        <div style={{
          marginBottom: 16,
          padding: "10px 12px",
          borderRadius: 6,
          border: `1.5px solid ${testResult.ok ? "#16a34a" : "#ef4444"}`,
          background: testResult.ok ? "#f0fdf4" : "#fef2f2",
          fontSize: 12,
          color: testResult.ok ? "#166534" : "#991b1b",
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {testResult.ok ? "Connection OK" : `Test failed${testResult.status ? ` (HTTP ${testResult.status})` : ""}`}
          </div>
          <div>{testResult.message}</div>
          {testResult.endpoint && <div style={{ marginTop: 4, opacity: 0.8 }}>Endpoint: {testResult.endpoint}</div>}
          {testResult.model && <div style={{ opacity: 0.8 }}>Model: {testResult.model}</div>}
          {testResult.upstreamBody && (
            <pre style={{ marginTop: 6, padding: 6, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 120, overflow: "auto" }}>
              {testResult.upstreamBody}
            </pre>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleSave}
          disabled={saving || testing}
          style={{ padding: "8px 22px", fontSize: 13, fontWeight: 600, background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 6, cursor: (saving || testing) ? "default" : "pointer", opacity: (saving || testing) ? 0.7 : 1 }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={handleTest}
          disabled={saving || testing}
          title="Send a minimal ping to the endpoint and verify it speaks OpenAI chat-completions."
          style={{ padding: "8px 22px", fontSize: 13, fontWeight: 600, background: "#fff", color: "#1e3a5f", border: "1.5px solid #1e3a5f", borderRadius: 6, cursor: (saving || testing) ? "default" : "pointer", opacity: (saving || testing) ? 0.7 : 1 }}
        >
          {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
    </div>
  );
}

// ── Panel map ──────────────────────────────────────────────────────────────────

// ── Site URL panel ────────────────────────────────────────────────────────────

function validateSiteUrl(value) {
  const v = value.trim();
  if (!v) return { valid: false, error: "URL is required." };
  try {
    const u = new URL(v);
    if (u.protocol !== "http:" && u.protocol !== "https:")
      return { valid: false, error: `Unsupported scheme "${u.protocol.replace(":", "")}". Use http or https.` };
    if (v.endsWith("/"))
      return { valid: false, error: "Remove the trailing slash." };
    return { valid: true };
  } catch {
    return { valid: false, error: "Not a valid URL. Example: http://10.0.0.50 or https://depository.company.com" };
  }
}

// Same shape as validateSiteUrl, but empty is a legitimate value: it clears the
// override so download links use the origin of the request that asked for them.
function validateDownloadBaseUrl(value) {
  const v = value.trim();
  if (!v) return { valid: true };
  return validateSiteUrl(v);
}

function getSiteUrlWarnings(value) {
  const warnings = [];
  try {
    const u = new URL(value.trim());
    const h = u.hostname.toLowerCase();
    if (h === "localhost" || h === "127.0.0.1" || h === "::1")
      warnings.push({ level: "error", text: "This points to localhost — remote clients will not be able to connect. Enter the server's IP address or hostname instead." });
    else if (u.protocol === "http:")
      warnings.push({ level: "warn", text: "HTTP is not recommended for production. Consider using HTTPS." });
  } catch { /* invalid URL — handled by validateSiteUrl */ }
  return warnings;
}

function SiteUrlPanel({ token }) {
  const [current, setCurrent]       = useState(null);
  const [currentDownload, setCurrentDownload] = useState("");
  const [downloadInput, setDownloadInput]     = useState("");
  const [affectedFiles, setAffected] = useState([]);
  const [isProduction, setIsProd]   = useState(true);
  const [input, setInput]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [result, setResult]         = useState(null);
  const [confirm, setConfirm]       = useState(false);

  useEffect(() => {
    getSiteUrl(token).then(data => {
      if (!data) return;
      if (data.siteUrl) { setCurrent(data.siteUrl); setInput(data.siteUrl); }
      const dl = data.downloadBaseUrl ?? "";
      setCurrentDownload(dl);
      setDownloadInput(dl);
      if (data.affectedFiles) setAffected(data.affectedFiles);
      if (data.isProduction !== undefined) setIsProd(data.isProduction);
    }).catch(() => {});
  }, [token]);

  const trimmed     = input.trim();
  const validation  = validateSiteUrl(trimmed);
  const inlineWarns = trimmed ? getSiteUrlWarnings(trimmed) : [];

  const downloadTrimmed    = downloadInput.trim();
  const downloadValidation = validateDownloadBaseUrl(downloadTrimmed);
  const downloadDirty      = downloadTrimmed !== (currentDownload ?? "");
  // Only a non-empty download URL can be unreachable; empty means "use the request origin".
  const downloadWarns      = downloadTrimmed ? getSiteUrlWarnings(downloadTrimmed) : [];

  const siteDirty   = trimmed !== (current ?? "");
  const isDirty     = siteDirty || downloadDirty;
  const canSave     = validation.valid && downloadValidation.valid && isDirty && !saving &&
                      !inlineWarns.some(w => w.level === "error") &&
                      !downloadWarns.some(w => w.level === "error");

  async function doSave() {
    setConfirm(false);
    setSaving(true);
    setResult(null);
    try {
      // Send the download URL only when it actually changed, so an unrelated Site URL
      // save leaves PublicDownloadBaseUrl alone rather than rewriting it.
      const res = await reconfigureSiteUrl(
        token, trimmed, downloadDirty ? downloadTrimmed : undefined);
      if (res.success) {
        setCurrent(trimmed);
        if (downloadDirty) setCurrentDownload(downloadTrimmed);
      }
      setResult(res);
    } catch (e) {
      setResult({ success: false, message: e.message, files: [], warnings: [] });
    } finally {
      setSaving(false);
    }
  }

  const downloadInputBorderColor = !downloadTrimmed ? undefined
    : !downloadValidation.valid ? "#ef4444"
    : downloadWarns.some(w => w.level === "error") ? "#ef4444"
    : downloadWarns.some(w => w.level === "warn") ? "#f59e0b"
    : "#22c55e";

  const inputBorderColor = !trimmed ? undefined
    : !validation.valid ? "#ef4444"
    : inlineWarns.some(w => w.level === "error") ? "#ef4444"
    : inlineWarns.some(w => w.level === "warn") ? "#f59e0b"
    : "#22c55e";

  return (
    <div className="indexes-main" style={{ maxWidth: 580 }}>

      {/* Description */}
      <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
        The public URL remote clients use to reach this server. Changing it updates:
        <ul style={{ margin: "8px 0 0 18px", padding: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <li><strong>ui/index.html</strong> — tells the browser where the API and auth service are (takes effect immediately on next page load)</li>
          <li><strong>api/appsettings.json</strong> — CORS allowed origin (requires service restart)</li>
          <li><strong>auth/appsettings.json</strong> — password-reset and email-confirmation links (requires service restart)</li>
        </ul>
        <div style={{ marginTop: 10 }}>
          The download link base URL below is separate and optional — it only affects the
          links returned with search results.
        </div>
      </div>

      {!isProduction && (
        <Alert type="info">
          This appears to be a development environment — ui/index.html was not found. Only appsettings files will be updated.
        </Alert>
      )}

      {/* Input */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Site URL
        </label>
        <input
          type="url"
          value={input}
          onChange={e => { setInput(e.target.value); setResult(null); }}
          placeholder="http://10.0.0.50  or  https://depository.yourcompany.com"
          style={{
            width: "100%",
            padding: "7px 10px",
            border: `1.5px solid ${inputBorderColor || "#e5e7eb"}`,
            borderRadius: 6,
            fontSize: 13,
            boxSizing: "border-box",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          No trailing slash · Include port if not 80/443 (e.g. http://10.0.0.50:8080)
        </div>
      </div>

      {/* Inline validation error */}
      {trimmed && !validation.valid && (
        <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10 }}>
          ⚠ {validation.error}
        </div>
      )}

      {/* Inline warnings (localhost / HTTP) */}
      {inlineWarns.map((w, i) => (
        <Alert key={i} type={w.level === "error" ? "error" : "info"}>
          {w.text}
        </Alert>
      ))}

      {/* Download link base URL — ApplicationSettings:PublicDownloadBaseUrl */}
      <div style={{ marginBottom: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
        <label htmlFor="downloadBaseUrl" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Download link base URL <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
        </label>
        <input
          id="downloadBaseUrl"
          type="url"
          value={downloadInput}
          onChange={e => { setDownloadInput(e.target.value); setResult(null); }}
          placeholder="Leave empty to use the address each request arrives on"
          style={{
            width: "100%",
            padding: "7px 10px",
            border: `1.5px solid ${downloadInputBorderColor || "#e5e7eb"}`,
            borderRadius: 6,
            fontSize: 13,
            boxSizing: "border-box",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, lineHeight: 1.6 }}>
          Host stamped into the time-limited download links returned with search results
          (<code style={{ fontSize: 10 }}>ApplicationSettings:PublicDownloadBaseUrl</code> in{" "}
          <code style={{ fontSize: 10 }}>api/appsettings.json</code>).
          Set this when callers reach the API through a gateway that does not forward the
          original host — otherwise leave it empty. No path, no trailing slash.
          Applies to new links immediately; no restart.
        </div>
      </div>

      {/* Inline validation error — download link base URL */}
      {downloadTrimmed && !downloadValidation.valid && (
        <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10 }}>
          ⚠ {downloadValidation.error}
        </div>
      )}

      {/* Inline warnings — download link base URL */}
      {downloadWarns.map((w, i) => (
        <Alert key={`dl${i}`} type={w.level === "error" ? "error" : "info"}>
          Download link base URL: {w.text}
        </Alert>
      ))}

      {/* Files that will be affected */}
      {affectedFiles.length > 0 && siteDirty && validation.valid && (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14,
          background: "#f9fafb", border: "1px solid #e5e7eb",
          borderRadius: 6, padding: "8px 12px" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Files that will be updated:</div>
          {affectedFiles.map(f => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <span style={{ color: "#9ca3af" }}>▸</span>
              <code style={{ fontSize: 11 }}>{f}</code>
              {f.includes("appsettings") && <span style={{ color: "#f59e0b", fontSize: 11 }}>— requires restart</span>}
              {f === "ui/index.html"      && <span style={{ color: "#22c55e", fontSize: 11 }}>— immediate</span>}
            </div>
          ))}
        </div>
      )}

      {/* Save result */}
      {result && (
        <div style={{ marginBottom: 14 }}>
          {/* Per-file outcomes */}
          {result.files?.length > 0 && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
              borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
              {result.files.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: i > 0 ? 4 : 0 }}>
                  <span style={{ color: f.ok ? (f.changed ? "#22c55e" : "#9ca3af") : "#ef4444", fontWeight: 700, marginTop: 1 }}>
                    {f.ok ? (f.changed ? "✓" : "–") : "✗"}
                  </span>
                  <div>
                    <code style={{ fontSize: 11 }}>{f.file}</code>
                    {f.ok && f.changed  && <span style={{ color: "#22c55e",  marginLeft: 6 }}>updated</span>}
                    {f.ok && !f.changed && <span style={{ color: "#9ca3af",  marginLeft: 6 }}>already correct</span>}
                    {!f.ok && f.error   && <div style={{ color: "#ef4444", marginTop: 2 }}>{f.error}</div>}
                    {f.ok && f.requiresRestart && f.changed &&
                      <span style={{ color: "#f59e0b", marginLeft: 6 }}>— restart required</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Alert type={result.success ? "success" : "error"} onClose={() => setResult(null)}>
            {result.message}
            {result.success && result.restartRequired && (
              <div style={{ fontSize: 12, marginTop: 6 }}>
                Restart the <strong>depository-api</strong> and <strong>depository-auth</strong> services for changes to take effect.
                The UI change (index.html) is already live on the next page load.
              </div>
            )}
          </Alert>

          {result.warnings?.map((w, i) => (
            <Alert key={i} type="info" onClose={() => {}}>⚠ {w}</Alert>
          ))}
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem",
            maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,.18)" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
              {siteDirty ? "Confirm Site URL change" : "Confirm download link base URL change"}
            </div>
            {siteDirty && (
              <>
                <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
                  <span style={{ color: "#6b7280" }}>From:</span> <code>{current || "(not set)"}</code>
                </div>
                <div style={{ fontSize: 13, color: "#374151", marginBottom: downloadDirty ? 12 : 16 }}>
                  <span style={{ color: "#6b7280" }}>To:</span> <code>{trimmed}</code>
                </div>
              </>
            )}
            {downloadDirty && (
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  Download link base URL
                </div>
                <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
                  <span style={{ color: "#6b7280" }}>From:</span> <code>{currentDownload || "(not set — uses the request address)"}</code>
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>
                  <span style={{ color: "#6b7280" }}>To:</span> <code>{downloadTrimmed || "(not set — uses the request address)"}</code>
                </div>
              </div>
            )}
            {inlineWarns.some(w => w.level === "warn") && (
              <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb",
                border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 12px", marginBottom: 14 }}>
                {inlineWarns.find(w => w.level === "warn")?.text}
              </div>
            )}
            {siteDirty ? (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                The <strong>depository-api</strong> and <strong>depository-auth</strong> services will need a restart after this change.
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                Only the download link base URL changes — no service restart is needed.
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={doSave}
                style={{ padding: "8px 22px", fontSize: 13, fontWeight: 600, background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                Confirm & Save
              </button>
              <button
                onClick={() => setConfirm(false)}
                style={{ padding: "8px 22px", fontSize: 13, fontWeight: 600, background: "#fff", color: "#1e3a5f", border: "1.5px solid #1e3a5f", borderRadius: 6, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setConfirm(true)}
        disabled={!canSave}
        style={{
          padding: "8px 22px",
          fontSize: 13,
          fontWeight: 600,
          background: "#1e3a5f",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: canSave ? "pointer" : "default",
          opacity: canSave ? 1 : 0.7,
        }}
      >
        {saving ? "Saving…" : "Save Site URL"}
      </button>
    </div>
  );
}

const PANELS = {
  ingestFileTypes: IngestFileTypesPanel,
  outputFileTypes: OutputFileTypesPanel,
  conversionApis: ConversionApisPanel,
  orgAiSettings: OrgAiSettingsPanel,
  siteUrl: SiteUrlPanel,
};

// ── Page ──────────────────────────────────────────────────────────────────────

function IconExport() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 1v8M5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconImport() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 9V1M5 4l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

export default function Definitions() {
  const { token } = useAuth();
  const [activeKey, setActiveKey] = useState(CATEGORIES[0].key);
  const [defMsg, setDefMsg] = useState(null); // { type, text }
  const importFileRef = useRef(null);

  const ActivePanel = PANELS[activeKey] ?? null;

  async function handleExportDefinitions() {
    try {
      const snapshot = await exportDefinitionsConfig(token);
      const json = JSON.stringify(snapshot, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `definitions.depconfig.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDefMsg({ type: "error", text: e?.message || "Export failed." });
    }
  }

  async function handleImportDefinitions(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const text = await file.text();
      const config = JSON.parse(text);
      const result = await importDefinitionsConfig(token, config);
      const parts = [];
      if (result.ingestAdded || result.ingestSkipped)
        parts.push(`Ingest: +${result.ingestAdded} / ${result.ingestSkipped} skipped`);
      if (result.outputAdded || result.outputSkipped)
        parts.push(`Output: +${result.outputAdded} / ${result.outputSkipped} skipped`);
      if (result.convAdded || result.convSkipped)
        parts.push(`APIs: +${result.convAdded} / ${result.convSkipped} skipped`);
      setDefMsg({ type: "success", text: parts.length ? parts.join(" · ") : "Import complete (nothing new)." });
    } catch (e) {
      setDefMsg({
        type: "error",
        text: e instanceof SyntaxError ? "Invalid JSON file." : (e?.message || "Import failed."),
      });
    }
  }

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container">
        <div className="indexes-page">

          {/* ── Page header ── */}
          <Hero
            title="Definitions"
            subtitle="Configure system-wide lookup tables and reference data."
            logo={<SchemaArt />}
            actions={
              <>
                <button type="button" className="asf-hero__btn" onClick={handleExportDefinitions} title="Export definitions config">
                  <IconExport /> Export
                </button>
                <button type="button" className="asf-hero__btn" onClick={() => importFileRef.current?.click()} title="Import definitions config">
                  <IconImport /> Import
                </button>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json,.depconfig.json"
                  style={{ display: "none" }}
                  onChange={handleImportDefinitions}
                />
              </>
            }
          />

          {defMsg && (
            <Alert type={defMsg.type} onClose={() => setDefMsg(null)}>{defMsg.text}</Alert>
          )}

          {/* ── Card shell ── */}
          <div className="indexes-shell">
            <div className="indexes-card">
              <div className="indexesShell">

                {/* ── Sidebar ── */}
                <div className="db-column dbRail">
                  <div className="dbRailHeader">
                    <div className="db-stack-header">
                      <div className="db-track-label">
                        <span>Definitions</span>
                      </div>
                    </div>
                  </div>
                  <div className="dbTabsRegion">
                    <div className="dbTabStack">
                      {CATEGORIES.map(cat => (
                        <div key={cat.key} className="dbTabRow">
                          <button
                            type="button"
                            className={`dbTab${activeKey === cat.key ? " isActive" : ""}`}
                            onClick={() => setActiveKey(cat.key)}
                          >
                            <span className="dbTabLabel">
                              <span className="dbTabLabelText">{cat.label}</span>
                              {activeKey === cat.key && cat.description && (
                                <span className="dbTabLabelCount">{cat.description}</span>
                              )}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Content panel ── */}
                <div className="dbMainPanel open">
                  {ActivePanel ? (
                    <ActivePanel token={token} />
                  ) : (
                    <div className="indexes-main">
                      <p className="indexes-empty-state">No content for this category yet.</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
